import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

const IMG =
  "https://images.unsplash.com/photo-1592317460245-765f641e122b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user?.must_change_password) {
        toast.info("Please set your own password to continue.");
        navigate(`/set-password?email=${encodeURIComponent(form.email)}`);
        return;
      }
      toast.success("Welcome back.");
      navigate(user?.role === "superadmin" ? "/admin" : "/analytics");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-base-bg/25 py-3 text-base-bg placeholder:text-base-bg/40 focus:border-sand focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-emerald-950">
      {/* Left: image */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG} alt="Irish landscape" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-emerald-950/55" aria-hidden="true" />
        <div className="absolute bottom-12 left-10 right-10">
          <p className="font-serif text-4xl text-base-bg leading-tight">TILT Ventures</p>
          <p className="mt-3 text-base-bg/70 max-w-sm">Internal insight portal. Access restricted to the TILT team.</p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
          data-testid="login-card"
        >
          <button
            data-testid="login-home-button"
            onClick={() => navigate("/")}
            className="mb-8 inline-flex items-center gap-2 text-base-bg/60 text-[0.72rem] uppercase tracking-[0.18em] hover:text-base-bg transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </button>
          <div className="flex items-center gap-2 text-sand text-[0.72rem] uppercase tracking-[0.22em] mb-6">
            <Lock className="h-3.5 w-3.5" /> Team access
          </div>
          <h1 className="font-serif text-4xl text-base-bg leading-none">Sign in</h1>
          <p className="mt-4 text-base-bg/60 text-sm">
            Access is by invitation only, restricted to <span className="text-sand">@tiltventures.ie</span> team members.
          </p>

          <form onSubmit={submit} data-testid="login-form" className="mt-10 space-y-8">
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Email</label>
              <input data-testid="login-email" type="email" value={form.email} onChange={update("email")} placeholder="you@tiltventures.ie" className={field} />
            </div>
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Password</label>
              <input data-testid="login-password" type="password" value={form.password} onChange={update("password")} placeholder="••••••••" className={field} />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="group inline-flex w-full items-center justify-center gap-3 bg-base-bg px-8 py-4 text-sm uppercase tracking-[0.18em] text-emerald-950 disabled:opacity-60"
            >
              {loading ? "Please wait..." : "Sign in"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>

          <p className="mt-8 text-sm text-base-bg/40">
            Need access? Contact your TILT administrator.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
